import * as React from 'react';
import { SectionProps } from './SharedSections/BookingDetailSection';
import BookingDetailsQrCode, { BookingDetailsQrCodeProps } from './BookingDetailsQrCode';
import { CustomerInfoSection } from './SharedSections/CustomerInfoSection';
import { PassengerInfoSection } from './FerryBookingDetailedView/Sections/PassengerInfoSection';
import { VehicleInfoSection } from './FerryBookingDetailedView/Sections/VehicleInfoSection';
import { TrailerInfoSection } from './FerryBookingDetailedView/Sections/TrailerInfoSection';
import { AdditionalNotesInfoSection } from './FerryBookingDetailedView/Sections/AdditionalNotesInfoSection';
import { DynamicFieldsInfoSection } from './FerryBookingDetailedView/Sections/DynamicFieldsInfoSection';
import { CustomTicketInfoSection } from './EventBookingDetailedView/Sections/CustomTicketInfoSection';
import { EventDateTimeInfoSection } from './EventBookingDetailedView/Sections/EventDateTimeInfoSection';
import { FreightDetailsSection } from './FreightBookingDetailedView/Sections/FreightDetailsSection';
import { ViewOtherBookingButton, ViewOtherBookingButtonProps } from './SharedSections/ViewOtherBookingButton';
import { FreightReceiverContactDetailSection } from './FreightBookingDetailedView/Sections/FreightReceiverContactDetailSection';
import { FreightNotifyContactDetailSection } from './FreightBookingDetailedView/Sections/FreightNotifyContactDetailSection';
import { AdditionalBookingOptionsInfoSection } from './FerryBookingDetailedView/Sections/AdditionalBookingOptionsInfoSection';
import { FerryTripDetailsSection } from './SharedSections/FerryTripDetailsSection';
import { TrackingInformationSection } from './FreightBookingDetailedView/Sections/TrackingInformationSection';

export type ComponentPropsConfig = {
	// Shared booking fields
	CustomerInfoSection: SectionProps;
	YourTicketInfoSection: BookingDetailsQrCodeProps;
	ViewOtherBookingSection: ViewOtherBookingButtonProps;
	FerryTripDetailsSection: SectionProps;

	// Vehicle/passenger booking fields
	PassengerInfoSection: SectionProps;
	VehicleInfoSection: SectionProps;
	TrailerInfoSection: SectionProps;
	AdditionalBookingOptionsInfoSection: SectionProps;
	AdditionalNotesInfoSection: SectionProps;
	DynamicFieldsInfoSection: SectionProps;

	// Event booking fields
	EventTicketInfoSection: SectionProps;
	EventDateTimeInfoSection: SectionProps;

	// Freight booking fields
	FreightDetailsSection: SectionProps;
	FreightReceiverContactDetailSection: SectionProps;
	FreightNotifyContactDetailSection: SectionProps;
	FreightTrackingInformationSection: SectionProps;
};

export const fieldComponents: {
	[K in keyof ComponentPropsConfig]: React.FC<ComponentPropsConfig[K]>;
} = {
	// Shared booking fields
	CustomerInfoSection: props => <CustomerInfoSection {...props} />,
	YourTicketInfoSection: props => <BookingDetailsQrCode {...props} />,
	ViewOtherBookingSection: props => <ViewOtherBookingButton {...props} />,
	FerryTripDetailsSection: props => <FerryTripDetailsSection {...props} />,

	// Vehicle/passenger booking fields
	PassengerInfoSection: props => <PassengerInfoSection {...props} />,
	VehicleInfoSection: props => <VehicleInfoSection {...props} />,
	TrailerInfoSection: props => <TrailerInfoSection {...props} />,
	AdditionalBookingOptionsInfoSection: props => <AdditionalBookingOptionsInfoSection {...props} />,
	AdditionalNotesInfoSection: props => <AdditionalNotesInfoSection {...props} />,
	DynamicFieldsInfoSection: props => <DynamicFieldsInfoSection {...props} />,

	// Event booking fields
	EventTicketInfoSection: props => <CustomTicketInfoSection {...props} />,
	EventDateTimeInfoSection: props => <EventDateTimeInfoSection {...props} />,

	// Freight booking fields
	FreightTrackingInformationSection: props => <TrackingInformationSection {...props} />,
	FreightDetailsSection: props => <FreightDetailsSection {...props} />,
	FreightNotifyContactDetailSection: props => <FreightNotifyContactDetailSection {...props} />,
	FreightReceiverContactDetailSection: props => <FreightReceiverContactDetailSection {...props} />,
};

export function getBookingDetailedViewComponents(
	sectionProps: SectionProps,
	booking: any,
	typeOfBooking: string,
	refresh: () => void,
) {
	const dynamicPropsConfig: Record<
		string,
		Array<{
			name: keyof ComponentPropsConfig;
			props: Partial<ComponentPropsConfig[keyof ComponentPropsConfig]>;
		}>
	> = {
		PASSENGER_ONLY: [
			{
				name: 'CustomerInfoSection',
				props: sectionProps,
			},
			{
				name: 'YourTicketInfoSection',
				props: {
					qrCodeId: booking.qrCodeId,
					isEventBooking: typeOfBooking === 'EVENT_BOOKING',
					showQrCode: sectionProps?.showQrCode ?? true,
				},
			},
			{
				name: 'ViewOtherBookingSection',
				props: {
					booking: booking,
					refresh: refresh,
				},
			},
			{
				name: 'FerryTripDetailsSection',
				props: sectionProps,
			},
			{
				name: 'PassengerInfoSection',
				props: sectionProps,
			},
			{
				// The booking note now renders inside DynamicFieldsInfoSection
				// ("Additional information"), so it is no longer a separate section.
				name: 'DynamicFieldsInfoSection',
				props: sectionProps,
			},
			{
				name: 'AdditionalBookingOptionsInfoSection',
				props: sectionProps,
			},
		],
		VEHICLE: [
			{
				name: 'CustomerInfoSection',
				props: sectionProps,
			},
			{
				name: 'YourTicketInfoSection',
				props: {
					qrCodeId: booking.qrCodeId,
					isEventBooking: typeOfBooking === 'EVENT_BOOKING',
					showQrCode: sectionProps?.showQrCode ?? true,
				},
			},
			{
				name: 'ViewOtherBookingSection',
				props: {
					booking: booking,
					refresh: refresh,
				},
			},
			{
				name: 'FerryTripDetailsSection',
				props: sectionProps,
			},
			{
				name: 'PassengerInfoSection',
				props: sectionProps,
			},
			{
				name: 'VehicleInfoSection',
				props: sectionProps,
			},
			{
				// Renders only when the booking has a trailer (otherwise the section is empty).
				name: 'TrailerInfoSection',
				props: sectionProps,
			},
			{
				// The booking note now renders inside DynamicFieldsInfoSection
				// ("Additional information"), so it is no longer a separate section.
				name: 'DynamicFieldsInfoSection',
				props: sectionProps,
			},
			{
				name: 'AdditionalBookingOptionsInfoSection',
				props: sectionProps,
			},
		],
		EVENT_BOOKING: [
			{
				name: 'CustomerInfoSection',
				props: sectionProps,
			},
			{
				name: 'YourTicketInfoSection',
				props: {
					qrCodeId: booking.qrCodeId,
					isEventBooking: typeOfBooking === 'EVENT_BOOKING',
					showQrCode: sectionProps?.showQrCode ?? true,
				},
			},
			{
				name: 'EventDateTimeInfoSection',
				props: sectionProps,
			},
			{
				name: 'EventTicketInfoSection',
				props: sectionProps,
			},
		],
		FREIGHT: [
			{
				name: 'CustomerInfoSection',
				props: sectionProps,
			},
			{
				name: 'FerryTripDetailsSection',
				props: sectionProps,
			},
			{
				name: 'YourTicketInfoSection',
				props: {
					qrCodeId: booking.qrCodeId,
					isEventBooking: typeOfBooking === 'EVENT_BOOKING',
					showQrCode: sectionProps?.showQrCode ?? true,
				},
			},
			{
				name: 'FreightTrackingInformationSection',
				props: sectionProps,
			},
			{
				name: 'FreightDetailsSection',
				props: sectionProps,
			},
			{
				name: 'FreightReceiverContactDetailSection',
				props: sectionProps,
			},
			{
				name: 'FreightNotifyContactDetailSection',
				props: sectionProps,
			},
			{
				// Admin-configured dynamic field values ("Additional information") —
				// collected on the freight Contact tab in the booking wizard.
				name: 'DynamicFieldsInfoSection',
				props: sectionProps,
			},
			{
				name: 'AdditionalBookingOptionsInfoSection',
				props: sectionProps,
			},
		],
	};
	return dynamicPropsConfig;
}
